import React from 'react';
import { 
    Box, 
    Button, 
    Alert, 
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    TextField,
    Autocomplete,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { 
    Close as CloseIcon, 
    CloudUpload as CloudUploadIcon, 
    ArrowBack as ArrowBackIcon, 
    ArrowForward as ArrowForwardIcon, 
    Upload as UploadIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { TableElement, CloseButton } from '../../_Components';

const UploadFileModal = ({ 
    isOpen, 
    onClose, 
    currentStep, 
    selectedFile, 
    parsedData, 
    fieldMapping, 
    phoneFields, 
    selectedCard,
    isProcessing,
    isSaving,
    uploadStatus,
    isDragOver,
    onFileSelect,
    onDragOver,
    onDragLeave,
    onDrop,
    onUploadAndParse,
    onFieldMappingChange,
    onPhoneFieldChange,
    onCardSelect,
    onSaveResults,
    onGoToPreviousStep,
    onNextStep,
    formValues,
    transformDataForServer
}) => {

    return (
        <Dialog 
            open={isOpen} 
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {currentStep === 1 && 'Загрузка файла'}
                        {currentStep === 2 && 'Предварительный просмотр'}
                        {currentStep === 3 && 'Подтверждение и сохранение'}

                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                
                {/* Индикатор прогресса */}
                <Box mt={2} display="flex" justifyContent="center">
                    <Box display="flex" gap={1}>
                        {[1, 2, 3].map((step) => (
                            <Box
                                key={step}
                                width={12}
                                height={12}
                                borderRadius="50%"
                                bgcolor={step <= currentStep ? "primary.main" : "grey.300"}
                                sx={{ transition: 'all 0.3s ease' }}
                            />
                        ))}
                    </Box>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {/* Шаг 1: Загрузка файла */}
                {currentStep === 1 && (
                    <Box mt={2}>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Выберите файл Excel (.xlsx) для загрузки и проверки данных
                        </Typography>
                    
                        <Box 
                            border={2} 
                            borderColor={isDragOver ? "primary.main" : "grey.300"}
                            borderRadius={2} 
                            p={3} 
                            textAlign="center"
                            mb={2}
                            bgcolor={isDragOver ? "primary.50" : "transparent"}
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            sx={{ borderStyle: "dashed" }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Перетащите файл сюда или нажмите для выбора
                            </Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Поддерживаются файлы .xlsx размером до 10MB
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                disabled={isProcessing}
                            >
                                Выбрать файл
                                <input
                                    type="file"
                                    hidden
                                    accept=".xlsx"
                                    onChange={onFileSelect}
                                />
                            </Button>
                        </Box>

                        {selectedFile && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Выбран файл:</strong> {selectedFile.name} 
                                    ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                            </Alert>
                        )}

                        {uploadStatus && (
                            <Alert severity={uploadStatus.status === 'error' ? 'error' : 'success'} sx={{ mb: 2 }}>
                                {uploadStatus.message}
                            </Alert>
                        )}
                    </Box>
                )}

                {/* Шаг 2: Предварительный просмотр */}
                {currentStep === 2 && parsedData && (
                    <Box mt={2}>
                        <Typography variant="h6" gutterBottom>
                            Результаты парсинга файла
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Файл успешно обработан. Просмотрите данные перед продолжением.
                        </Typography>

                        <Box mb={3}>
                            <Typography variant="subtitle1" gutterBottom>
                                Статистика файла:
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Листов: {parsedData.stats?.sheets || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Всего строк: {parsedData.stats?.totalRows || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Обработано строк: {parsedData.data?.metadata?.processedRows || 0}
                            </Typography>
                        </Box>

                        <Box mb={3}>
                            <Typography variant="subtitle1" gutterBottom>
                                Заголовки файла:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {parsedData.data?.headers?.map((header, index) => (
                                    <Box
                                        key={header.index}
                                        p={1}
                                        bgcolor="primary.50"
                                        borderRadius={1}
                                        border={1}
                                        borderColor="primary.200"
                                    >
                                        <Typography variant="caption">
                                            {header.index}: {header.name}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Первые записи:
                            </Typography>
                            <TableElement
                                header={parsedData.data?.headers?.map(header => ({
                                    label: header.name,
                                    value: header.index.toString(),
                                    width: 20
                                })) || []}
                                rows={parsedData.data?.rows?.map(row => {
                                    const rowData = {};
                                    Object.keys(row.data).forEach(key => {
                                        rowData[key] = row.data[key];
                                    });
                                    return rowData;
                                }) || []}
                                edit={false}
                                remove={false}
                            />
                        </Box>
                    </Box>
                )}

                {/* Шаг 3: Подтверждение и сохранение */}
                {currentStep === 3 && parsedData && (
                    <Box mt={2}>

                        <Typography variant="h6" gutterBottom>
                            Настройка структуры данных
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Сопоставьте заголовки файла с полями системы и выберите карточку проекта.
                        </Typography>

                        {/* Выбор карточки проекта */}
                        <Box mb={3}>
                            <Typography variant="subtitle1" gutterBottom>
                                Карточка проекта *
                            </Typography>
                            <Autocomplete
                                options={formValues.cards || []}
                                getOptionLabel={(option) => option.name}
                                value={selectedCard}
                                onChange={(event, newValue) => onCardSelect(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Выберите карточку проекта"
                                        variant="standard"
                                        fullWidth
                                    />
                                )}
                            />
                        </Box>

                        {/* Маппинг полей */}
                        {parsedData.data?.headers && (
                            <Box mb={3}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Сопоставление заголовков с полями
                                </Typography>
                                

                                
                                <Box display="flex" flexDirection="column" gap={2}>
                                    {parsedData.data.headers.map((header, headerIndex) => (
                                        <Box key={header.index} display="flex" alignItems="center" gap={2}>
                                            <Box minWidth={120}>
                                                <Typography variant="body2" color="textSecondary">
                                                    {header.name}:
                                                </Typography>
                                            </Box>
                                            
                                            <Box flex={1}>
                                                <Autocomplete
                                                    options={formValues.fields || []}
                                                    getOptionLabel={(option) => option.label}
                                                    value={fieldMapping[header.index.toString()] || null}
                                                    onChange={(event, newValue) => onFieldMappingChange(header.index.toString(), newValue)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Выберите поле"
                                                            variant="standard"
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                            
                                            <Box>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={phoneFields[header.index.toString()] || false}
                                                            onChange={(event) => onPhoneFieldChange(header.index.toString(), event.target.checked)}
                                                        />
                                                    }
                                                    label="Телефон"
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Предварительный просмотр данных */}
                        {selectedCard && Object.keys(fieldMapping).length > 0 && (
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>
                                    Предварительный просмотр данных
                                </Typography>
                                
                                <Typography variant="body2" color="textSecondary" paragraph>
                                    Вот как будут выглядеть данные при отправке на сервер:
                                    {Object.keys(phoneFields).length > 0 && (
                                        <span style={{ color: 'info.main', fontWeight: 'bold' }}>
                                            {' '}Телефонные номера будут автоматически очищены от символов (останутся только цифры).
                                        </span>
                                    )}
                                </Typography>
                                
                                <Box maxHeight={200} overflow="auto" p={2} bgcolor="grey.50" borderRadius={1} border={1} borderColor="grey.300">
                                    <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                        {JSON.stringify(
                                            transformDataForServer(parsedData, fieldMapping, phoneFields, selectedCard)?.slice(0, 3) || [],
                                            null,
                                            2
                                        )}
                                    </pre>
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                        Показаны первые 3 записи из {parsedData.data.rows?.length || 0} всего
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                {/* Кнопка "Назад" для шагов 2 и 3 */}
                {currentStep > 1 && (
                    <Button 
                        onClick={onGoToPreviousStep} 
                        disabled={isProcessing || isSaving}
                        color="primary" 
                        size="small"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                    >
                        Назад
                    </Button>
                )}
                
                
                {/* Кнопка "Отмена" для всех шагов */}
                <CloseButton handleCloseClick={onClose} disabled={isProcessing || isSaving} label="Отмена"/>
                
                {/* Кнопки для разных шагов */}
                {currentStep === 1 && (
                    <Button 
                        onClick={onUploadAndParse} 
                        variant="contained" 
                        color="primary"
                        disabled={!selectedFile || isProcessing}
                        startIcon={isProcessing ? <CircularProgress size={20} /> : <UploadIcon />}
                    >
                        {isProcessing ? 'Обработка...' : 'Загрузить'}
                    </Button>
                )}
                
                {currentStep === 2 && (
                    <Button 
                        onClick={() => {
                            // Проверяем, что у нас есть все необходимые данные перед переходом
                            if (parsedData && parsedData.data && parsedData.data.headers) {
                                // Переходим к шагу 3
                                onNextStep();
                            }
                        }} 
                        endIcon={<ArrowForwardIcon />}
                        variant="contained" 
                        color="primary"
                        disabled={!parsedData}
                    >
                        Продолжить
                    </Button>
                )}
                
                {currentStep === 3 && (
                    <Button 
                        onClick={onSaveResults} 
                        variant="contained" 
                        color="primary"
                        disabled={!parsedData || !selectedCard || isSaving}
                        startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {isSaving ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default UploadFileModal;
